import { useState, useEffect, useMemo } from 'react'
import CurrencyCard from './CurrencyCard'
import FlagIcon from './FlagIcon'
import Keypad from './Keypad'
import KrwDenominationModal from './KrwDenominationModal'
import MemoInputModal from './MemoInputModal'
import LoadingModal from './LoadingModal'
import ConfirmModal from './ConfirmModal'
import { useExchange } from '../hooks/useExchange'
import { useBillCounter } from '../hooks/useBillCounter'
import { getSettlementStatus, closeSettlement } from '../api/services'
import { ApiError } from '../api/client'
import type { BundleItem, CurrencySettlementStatus } from '../api/types'
import type { CounterDenomination } from '@abcpay/shared'

const currencies = [
  { code: 'KRW', countryCode: 'kr', name: '대한민국 원' },
  { code: 'USD', countryCode: 'us', name: '미국 달러' },
  { code: 'JPY', countryCode: 'jp', name: '일본 엔' },
  { code: 'CNY', countryCode: 'cn', name: '중국 위안' },
  { code: 'EUR', countryCode: 'eu', name: '유로' },
  { code: 'HKD', countryCode: 'hk', name: '홍콩 달러' },
  { code: 'TWD', countryCode: 'tw', name: '대만 달러' },
  { code: 'SGD', countryCode: 'sg', name: '싱가포르 달러' },
  { code: 'THB', countryCode: 'th', name: '태국 바트' },
  { code: 'PHP', countryCode: 'ph', name: '필리핀 페소' },
  { code: 'VND', countryCode: 'vn', name: '베트남 동' },
  { code: 'GBP', countryCode: 'gb', name: '영국 파운드' },
  { code: 'AUD', countryCode: 'au', name: '호주 달러' },
  { code: 'CAD', countryCode: 'ca', name: '캐나다 달러' },
  { code: 'IDR', countryCode: 'id', name: '인도네시아 루피아' },
  { code: 'MYR', countryCode: 'my', name: '말레이시아 링깃' },
]

function CloseTab() {
  const { selectedBranch } = useExchange()
  const { lastCounterData, clearLastData } = useBillCounter()

  const [selectedCurrency, setSelectedCurrency] = useState<string>('KRW')
  const [actualAmount, setActualAmount] = useState<string>('')
  const [bundles, setBundles] = useState<BundleItem[]>([])
  const [looseTotal, setLooseTotal] = useState<number>(0)
  const [krwTotal, setKrwTotal] = useState<number>(0)
  const [looseBillCounts, setLooseBillCounts] = useState<Record<number, string>>({ 50000: '', 10000: '', 5000: '', 1000: '' })
  const [looseCoinCounts, setLooseCoinCounts] = useState<Record<number, string>>({ 500: '', 100: '' })

  const [pendingCounterDenominations, setPendingCounterDenominations] = useState<CounterDenomination[] | undefined>(undefined)

  const [settlementStatuses, setSettlementStatuses] = useState<CurrencySettlementStatus[]>([])
  const [isLoadingStatus, setIsLoadingStatus] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  const [showKrwModal, setShowKrwModal] = useState(false)
  const [showMemoModal, setShowMemoModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [resultData, setResultData] = useState<{
    isMatched: boolean
    adjustmentAmount: string | null
  } | null>(null)

  const todayDate = useMemo(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }, [])

  const selectedCurrencyInfo = useMemo(
    () => currencies.find((c) => c.code === selectedCurrency) ?? currencies[0],
    [selectedCurrency]
  )

  const currentStatus = useMemo(() => {
    return settlementStatuses.find((s) => s.currencyCode === selectedCurrency)
  }, [settlementStatuses, selectedCurrency])

  const systemBalance = useMemo(() => {
    if (!currentStatus) return 0
    return parseFloat(currentStatus.systemBalance) || 0
  }, [currentStatus])

  const calculatedActualAmount = useMemo(() => {
    if (selectedCurrency === 'KRW') {
      return krwTotal
    }
    return parseFloat(actualAmount) || 0
  }, [selectedCurrency, krwTotal, actualAmount])

  const difference = useMemo(() => {
    return calculatedActualAmount - systemBalance
  }, [calculatedActualAmount, systemBalance])

  const hasDifference = useMemo(() => {
    return difference !== 0
  }, [difference])

  const hasEnteredAmount = useMemo(() => {
    if (selectedCurrency === 'KRW') {
      return krwTotal > 0
    }
    return actualAmount !== '' && parseFloat(actualAmount) > 0
  }, [selectedCurrency, krwTotal, actualAmount])

  const getSettlementStatusForCurrency = (currencyCode: string): 'none' | 'settled' | 'settled-with-memo' => {
    const status = settlementStatuses.find((s) => s.currencyCode === currencyCode)
    if (!status?.lastSettlement) return 'none'

    const settledDate = new Date(status.lastSettlement.settledAt).toISOString().split('T')[0]
    if (settledDate !== todayDate) return 'none'

    if (status.lastSettlement.memo) {
      return 'settled-with-memo'
    }
    return 'settled'
  }

  const isSettledToday = useMemo(() => {
    if (!currentStatus?.lastSettlement) return false

    const settledDate = new Date(currentStatus.lastSettlement.settledAt).toISOString().split('T')[0]
    return settledDate === todayDate
  }, [currentStatus, todayDate])

  const lastSettlementMemo = useMemo(() => {
    if (!currentStatus?.lastSettlement) return null

    const settledDate = new Date(currentStatus.lastSettlement.settledAt).toISOString().split('T')[0]
    if (settledDate !== todayDate) return null

    return currentStatus.lastSettlement.memo
  }, [currentStatus, todayDate])

  const canSubmit = useMemo(() => {
    if (selectedCurrency === 'KRW') {
      return krwTotal > 0 || bundles.length > 0 || looseTotal > 0
    }
    return actualAmount !== '' && parseFloat(actualAmount) >= 0
  }, [selectedCurrency, krwTotal, bundles, looseTotal, actualAmount])

  const formatNumber = (value: number): string => {
    return value.toLocaleString('ko-KR')
  }

  const loadSettlementStatus = async () => {
    if (!selectedBranch) return

    setIsLoadingStatus(true)
    setStatusError(null)

    try {
      const response = await getSettlementStatus(selectedBranch.nid)
      setSettlementStatuses(response.currencies)
    } catch (error) {
      console.error('Failed to load settlement status:', error)
      if (error instanceof ApiError) {
        setStatusError(error.message)
      } else {
        setStatusError('정산 상태를 불러오는데 실패했습니다.')
      }
    } finally {
      setIsLoadingStatus(false)
    }
  }

  useEffect(() => {
    loadSettlementStatus()
  }, [selectedBranch])

  useEffect(() => {
    setActualAmount('')
    setBundles([])
    setLooseTotal(0)
    setKrwTotal(0)
    setLooseBillCounts({ 50000: '', 10000: '', 5000: '', 1000: '' })
    setLooseCoinCounts({ 500: '', 100: '' })
    setPendingCounterDenominations(undefined)
  }, [selectedCurrency])

  useEffect(() => {
    if (!lastCounterData) return
    if (isSettledToday) {
      clearLastData()
      return
    }

    const { currency, totalAmount, denominations } = lastCounterData

    if (currency === 'KRW') {
      if (selectedCurrency !== 'KRW') {
        setSelectedCurrency('KRW')
      }
      if (denominations && denominations.length > 0) {
        setPendingCounterDenominations(denominations)
      }
      setShowKrwModal(true)
    } else {
      const currencyExists = currencies.some((c) => c.code === currency)
      if (currencyExists) {
        setSelectedCurrency(currency)
        setActualAmount(totalAmount.toString())
      }
    }

    clearLastData()
  }, [lastCounterData, isSettledToday, selectedCurrency, clearLastData])

  const handleCurrencySelect = (code: string) => {
    setSelectedCurrency(code)
  }

  const handleKrwModalConfirm = (
    newBundles: BundleItem[],
    newLooseTotal: number,
    total: number,
    newLooseBillCounts: Record<number, string>,
    newLooseCoinCounts: Record<number, string>
  ) => {
    setBundles(newBundles)
    setLooseTotal(newLooseTotal)
    setKrwTotal(total)
    setLooseBillCounts(newLooseBillCounts)
    setLooseCoinCounts(newLooseCoinCounts)
    setShowKrwModal(false)
  }

  const handleSubmitClick = () => {
    if (hasDifference) {
      setShowMemoModal(true)
    } else {
      submitSettlement()
    }
  }

  const submitSettlement = async (memo?: string) => {
    if (!selectedBranch) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const request = {
        branchNid: selectedBranch.nid,
        currencyCode: selectedCurrency,
        bundles: selectedCurrency === 'KRW' ? bundles : [],
        looseTotal: selectedCurrency === 'KRW' ? looseTotal : parseFloat(actualAmount) || 0,
        memo,
      }

      const response = await closeSettlement(request)

      setResultData({
        isMatched: response.settlement.isMatched,
        adjustmentAmount: response.settlement.adjustmentAmount,
      })
      setShowResultModal(true)

      loadSettlementStatus()

      setActualAmount('')
      setBundles([])
      setLooseTotal(0)
      setKrwTotal(0)
      setLooseBillCounts({ 50000: '', 10000: '', 5000: '', 1000: '' })
      setLooseCoinCounts({ 500: '', 100: '' })
    } catch (error) {
      console.error('Failed to close settlement:', error)
      if (error instanceof ApiError) {
        setSubmitError(error.message)
      } else {
        setSubmitError('마감 등록에 실패했습니다.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMemoConfirm = (memo: string) => {
    setShowMemoModal(false)
    submitSettlement(memo)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end items-center gap-2 text-fx-text-muted">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span>마감 날짜:</span>
        <span className="font-medium text-fx-text-dark">{todayDate}</span>
      </div>

      {statusError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between">
          <span>{statusError}</span>
          <button
            onClick={loadSettlementStatus}
            className="text-sm text-red-600 hover:text-red-800 underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {submitError}
        </div>
      )}

      <div className="flex gap-6 flex-1">
        <div className="flex-1 bg-fx-currency-bg p-6 rounded-lg">
          <div className="grid grid-cols-4 gap-4">
            {currencies.map((currency) => (
              <CurrencyCard
                key={currency.code}
                code={currency.code}
                flagCode={currency.countryCode}
                isSelected={selectedCurrency === currency.code}
                onClick={() => handleCurrencySelect(currency.code)}
                settlementStatus={getSettlementStatusForCurrency(currency.code)}
              />
            ))}
          </div>
        </div>

        <div className="w-72 lg:w-80 fhd:w-96 flex flex-col gap-4">
          <div className="bg-white p-6 rounded-lg border border-gray-200 flex flex-col gap-4 flex-1">
            <div className="flex flex-col items-center gap-2 pb-4 border-b border-gray-100">
              <FlagIcon
                countryCode={selectedCurrencyInfo.countryCode}
                size="xl"
                className="text-5xl"
                title={selectedCurrencyInfo.name}
              />
              <span className="text-3xl font-bold text-fx-text-dark">{selectedCurrency}</span>
              <span className="text-sm text-fx-text-muted">{selectedCurrencyInfo.name}</span>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-fx-text-muted mb-2">시스템 잔고</label>
              <div className="text-3xl font-bold text-fx-text-dark">
                {isLoadingStatus ? (
                  <span className="text-lg text-gray-400">로딩 중...</span>
                ) : (
                  formatNumber(systemBalance)
                )}
              </div>
            </div>

            {isSettledToday && currentStatus?.lastSettlement && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <label className="block text-sm font-medium text-green-700 mb-2">
                  정산 잔고{' '}
                  <span className="text-green-600 font-normal">
                    ({new Date(currentStatus.lastSettlement.settledAt).toLocaleDateString('ko-KR')})
                  </span>
                </label>
                <div className="text-2xl font-bold text-green-800">
                  {formatNumber(parseFloat(currentStatus.lastSettlement.actualAmount))}
                </div>
              </div>
            )}

            {!isSettledToday && (
              <div
                className={`p-4 rounded-lg border-2 ${
                  hasDifference && calculatedActualAmount > 0
                    ? 'border-red-200 bg-red-50'
                    : 'border-fx-buy-light bg-fx-buy-light'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-fx-text-muted">
                    실제 잔고{selectedCurrency === 'KRW' ? ' (원화 권종별)' : ''}
                  </label>
                  {selectedCurrency === 'KRW' && (
                    <button
                      onClick={() => setShowKrwModal(true)}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
                    >
                      권종별 입력
                    </button>
                  )}
                </div>

                {selectedCurrency === 'KRW' ? (
                  <div className="text-3xl font-bold text-fx-text-dark">
                    {krwTotal > 0 ? formatNumber(krwTotal) : <span className="text-fx-buy">입력 필요</span>}
                  </div>
                ) : (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={actualAmount ? formatNumber(parseFloat(actualAmount)) : ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '')
                      setActualAmount(val)
                    }}
                    className="w-full text-3xl font-bold text-fx-text-dark bg-transparent border-none focus:outline-none placeholder:text-gray-300"
                    placeholder="0"
                    readOnly
                  />
                )}
              </div>
            )}

            {isSettledToday && lastSettlementMemo && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <label className="block text-sm font-medium text-yellow-700 mb-1">오늘 정산 메모</label>
                <div className="text-sm text-yellow-800">{lastSettlementMemo}</div>
              </div>
            )}

            {!isSettledToday && hasEnteredAmount && hasDifference && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <label className="block text-sm font-medium text-red-600 mb-1">차액</label>
                <div className="text-2xl font-bold text-red-600">
                  {difference > 0 ? '+' : ''}
                  {formatNumber(difference)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="w-64 lg:w-72 fhd:w-80 flex flex-col gap-3">
          <Keypad
            value={actualAmount}
            onChange={setActualAmount}
            disabled={selectedCurrency === 'KRW' || isLoadingStatus || isSettledToday}
          />

          <button
            type="button"
            onClick={handleSubmitClick}
            disabled={!canSubmit || isSubmitting || isLoadingStatus || isSettledToday}
            className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-fx-confirm text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            마감 등록
          </button>
        </div>
      </div>

      <KrwDenominationModal
        isOpen={showKrwModal}
        initialBundles={bundles}
        initialLooseTotal={looseTotal}
        initialLooseBillCounts={looseBillCounts}
        initialLooseCoinCounts={looseCoinCounts}
        externalDenominations={pendingCounterDenominations}
        onConfirm={(newBundles, newLooseTotal, total, newLooseBillCounts, newLooseCoinCounts) => {
          handleKrwModalConfirm(newBundles, newLooseTotal, total, newLooseBillCounts, newLooseCoinCounts)
          setPendingCounterDenominations(undefined)
        }}
        onCancel={() => {
          setShowKrwModal(false)
          setPendingCounterDenominations(undefined)
        }}
      />

      <MemoInputModal
        isOpen={showMemoModal}
        difference={difference}
        currencyCode={selectedCurrency}
        onConfirm={handleMemoConfirm}
        onCancel={() => setShowMemoModal(false)}
      />

      <LoadingModal isOpen={isSubmitting} message="마감 등록 중..." />

      <ConfirmModal
        isOpen={showResultModal}
        title="마감 등록 완료"
        confirmText="확인"
        cancelText=""
        onConfirm={() => setShowResultModal(false)}
        onCancel={() => setShowResultModal(false)}
      >
        <div className="text-center space-y-4">
          {resultData?.isMatched ? (
            <div className="text-green-600">
              <svg className="w-16 h-16 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-bold">잔고가 일치합니다</p>
            </div>
          ) : (
            <div className="text-yellow-600">
              <svg className="w-16 h-16 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-lg font-bold">차액이 발생했습니다</p>
              {resultData?.adjustmentAmount && (
                <p className="text-sm text-gray-600 mt-1">
                  조정 금액: {formatNumber(parseFloat(resultData.adjustmentAmount))}
                </p>
              )}
            </div>
          )}
        </div>
      </ConfirmModal>
    </div>
  )
}

export default CloseTab
